import * as React from "react"
import type { InputNumberProps } from "./InputNumber.types"
import { Input, labelVariants } from "../Input/Input"
import { cn } from "../../lib/utils"

const MinusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

const inputSizeClassMap = {
  xs: "h-7 text-xs",
  sm: "h-8 text-sm",
  base: "h-9 text-base",
  l: "h-10 text-lg",
  xl: "h-11 text-xl",
};

const iconSizeClassMap = {
  xs: "w-3 h-3 text-inherit",
  sm: "w-3.5 h-3.5 text-inherit",
  base: "w-4 h-4 text-inherit",
  l: "w-4.5 h-4.5 text-inherit",
  xl: "w-5 h-5 text-inherit",
};

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  ({ className, onChange, min, max, step = 1, disabled, readOnly, value, defaultValue, error, label, description, helperText, errorMessage, actionText, id, required, showControls = false, inputSize = 'base', ...props }, ref) => {

    const inputRef = React.useRef<HTMLInputElement>(null);

    const setRefs = React.useCallback(
      (node: HTMLInputElement) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [ref]
    );

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<number | string | readonly string[]>(
      isControlled && value !== undefined ? value : (defaultValue !== undefined ? defaultValue : '')
    );

    React.useEffect(() => {
      if (isControlled && value !== undefined) {
        setInternalValue(value);
      }
    }, [value, isControlled]);

    const triggerChange = (newVal: number) => {
      if (!isControlled) {
        setInternalValue(newVal);
      }

      if (inputRef.current) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(inputRef.current, newVal.toString());

        const event = new Event('input', { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    };

    const handleIncrement = (e: React.MouseEvent) => {
      e.preventDefault();
      if (disabled || readOnly) return;

      const currentVal = Number(internalValue !== '' ? internalValue : 0);
      const nextVal = currentVal + Number(step);

      if (max !== undefined && nextVal > Number(max)) return;

      triggerChange(nextVal);
    };

    const handleDecrement = (e: React.MouseEvent) => {
      e.preventDefault();
      if (disabled || readOnly) return;

      const currentVal = Number(internalValue !== '' ? internalValue : 0);
      const nextVal = currentVal - Number(step);

      if (min !== undefined && nextVal < Number(min)) return;

      triggerChange(nextVal);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const [generatedId] = React.useState(() => Math.random().toString(36).substring(2, 9));
    const inputId = id || `input-number-${generatedId}`;

    if (!showControls) {
      return (
        <Input
          type="number"
          ref={setRefs}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          className={className}
          error={error}
          label={label}
          description={description}
          actionText={actionText}
          id={id}
          required={required}
          inputSize={inputSize}
          {...props}
        />
      );
    }

    return (
      <div className={cn("grid w-full items-center gap-2", className)}>
        {(label || actionText || description) && (
          <div className="flex flex-col gap-2 w-full">
            {(label || actionText) && (
              <div className="flex items-center justify-between w-full">
                {label && (
                  <label htmlFor={inputId} className={cn("text-content-primary dark:text-dark-content-primary", labelVariants({ size: inputSize }))}>
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                  </label>
                )}
                {actionText && (
                  <a href="#" className="text-xs font-medium text-primary dark:text-primary-300 cursor-pointer hover:underline">
                    {actionText}
                  </a>
                )}
              </div>
            )}
            {description && (
              <p className={cn(inputSize === 'xs' ? "text-xs" : "text-sm", error ? "text-destructive" : "text-muted-foreground")}>
                {description}
              </p>
            )}
          </div>
        )}

        <div
          className={cn(
            "flex items-center justify-between p-0.5 w-full sm:w-auto bg-white border rounded-md dark:bg-transparent transition-colors overflow-hidden",
            inputSizeClassMap[inputSize],
            error ? "border-destructive ring-1 ring-destructive" : "border-input dark:border-gray-700 focus-within:ring-1 focus-within:ring-primary-400 focus-within:border-primary-400",
            disabled && "opacity-50 cursor-not-allowed bg-zinc-100 dark:bg-zinc-800/80",
            readOnly && "bg-zinc-100 dark:bg-zinc-800/80 cursor-default"
          )}
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={handleDecrement}
            disabled={disabled || readOnly || (min !== undefined && Number(internalValue || 0) <= Number(min))}
            className="flex items-center justify-center h-full aspect-square shrink-0 rounded-sm text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-800 active:bg-zinc-200 dark:active:bg-zinc-700 active:scale-95 disabled:opacity-50 disabled:pointer-events-none focus:outline-none transition-colors"
            aria-label="Disminuir"
          >
            <MinusIcon className={iconSizeClassMap[inputSize]} />
          </button>

          <input
            {...props}
            id={inputId}
            ref={setRefs}
            type="number"
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            readOnly={readOnly}
            value={isControlled ? value : internalValue}
            onChange={handleChange}
            className={cn(
              "p-0 w-full min-w-[3rem] h-full text-center bg-transparent border-0 outline-none focus:outline-none focus:ring-0 text-zinc-800 dark:text-white",
              "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              disabled && "cursor-not-allowed",
              readOnly && "text-zinc-500 dark:text-zinc-400 focus:ring-0 cursor-default"
            )}
            style={{ MozAppearance: 'textfield' }}
          />

          <button
            type="button"
            tabIndex={-1}
            onClick={handleIncrement}
            disabled={disabled || readOnly || (max !== undefined && Number(internalValue || 0) >= Number(max))}
            className="flex items-center justify-center h-full aspect-square shrink-0 rounded-sm text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-800 active:bg-zinc-200 dark:active:bg-zinc-700 active:scale-95 disabled:opacity-50 disabled:pointer-events-none focus:outline-none transition-colors"
            aria-label="Aumentar"
          >
            <PlusIcon className={iconSizeClassMap[inputSize]} />
          </button>
        </div>

        {(helperText || errorMessage) && (
          <p className={cn(inputSize === 'xs' ? "text-xs" : "text-sm", error ? "text-destructive" : "text-muted-foreground")}>
            {error && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  }
)
InputNumber.displayName = "InputNumber"

export { InputNumber }
